import { gql } from '@apollo/client';
import { ExternalLink } from '@components/link';
import { useConfirmation, useModal } from '@components/modal';
import { Tooltip } from '@components/tooltip';
import {
  RemoteDevelopment,
  RemoteSessionOptionsFragmentFragment,
  useDeleteRemoteDevelopmentMutation,
  useGetSSHKeysQuery,
  useStartRemoteDevelopmentMutation,
  useStopRemoteDevelopmentMutation,
} from '@generated/graphql';
import { MenuItem, Typography } from '@mui/material';
import { CreateRemoteSessionForChannelModal } from '@subsets/profile/CreateRemoteSessionModal';
import posthog from 'posthog-js';
import { ShareRemoteSessionModal } from './ShareRemoteSessionModal';

export const RemoteSessionOptionsFragment = gql`
  fragment RemoteSessionOptionsFragment on RemoteDevelopment {
    channelId
    editorSessionId
    editorUrl
    sshPort
    status {
      state
    }
  }
`;

type RemoteSessionOptionsProps = {
  channelId: string;
  channelInstanceType: string;
  organizationId: string;
  session: RemoteSessionOptionsFragmentFragment;
  onClose: VoidFunction;
};

export const RemoteSessionOptions = ({
  channelId,
  channelInstanceType,
  organizationId,
  session,
  onClose,
}: RemoteSessionOptionsProps) => {
  const { data: sshKeyData } = useGetSSHKeysQuery();
  const hasSshKeys = sshKeyData?.getSSHKeys?.length > 0;

  if (
    !session?.editorUrl ||
    !session?.editorSessionId ||
    (session?.status?.state && session.status.state === 'DELETED')
  ) {
    return (
      <CreateSessionOption
        channelId={channelId}
        organizationId={organizationId}
        defaultInstanceType={channelInstanceType}
        onClose={onClose}
      />
    );
  }
  if (session?.status?.state === 'ERROR' || session?.status?.state === 'FAILED') {
    return <DeleteSessionOption {...session} onClose={onClose} />;
  }
  return (
    <>
      {session.status.state === 'STOPPED' ? (
        <StartSessionOption {...session} onClose={onClose} />
      ) : session.status.state === 'STOPPING' ? null : (
        <LaunchSessionOptions {...session} onClose={onClose} hasSSHKeys={hasSshKeys} />
      )}
      {session.status.state === 'RUNNING' && <StopSessionOption {...session} onClose={onClose} />}
      {session.status.state !== 'DELETED' && <ShareSessionOption {...session} onClose={onClose} />}
      {session.status.state !== 'DELETED' && <DeleteSessionOption {...session} onClose={onClose} />}
    </>
  );
};

type SharedRemoteSessionOptionProps = { channelId: string; onClose: VoidFunction };

const CreateSessionOption = ({
  channelId,
  defaultInstanceType,
  organizationId,
  onClose,
}: SharedRemoteSessionOptionProps & { organizationId: string; defaultInstanceType: string }) => {
  const { showModal: showCreateModal } = useModal({
    component: CreateRemoteSessionForChannelModal,
    componentProps: {
      channelId,
      organizationId,
      defaultInstanceType,
      onClose,
    },
    modalProps: {
      title: 'Create remote session',
    },
  });

  return (
    <MenuItem onClick={showCreateModal}>
      <Typography variant="caption2">Create new remote session</Typography>
    </MenuItem>
  );
};

const DeleteSessionOption = ({
  channelId,
  editorSessionId,
  onClose,
}: Pick<RemoteDevelopment, 'editorSessionId'> & SharedRemoteSessionOptionProps) => {
  const [deleteSession] = useDeleteRemoteDevelopmentMutation();
  const { showConfirmation } = useConfirmation();
  const onClick = () => {
    if (onClose) {
      onClose();
    }
    void showConfirmation({
      onAffirm: () => {
        posthog.capture('user_delete_remote-session', { channelId, editorSessionId });
        void deleteSession({ variables: { editorSessionId } });
      },
      message: `Are you sure you want to delete the remote session: ${editorSessionId}?`,
    });
  };
  return (
    <MenuItem onClick={onClick}>
      <Typography variant="caption2">Delete remote session</Typography>
    </MenuItem>
  );
};

export const LaunchSessionOptions = ({
  channelId,
  editorSessionId,
  editorUrl,
  hasSSHKeys,
  status,
  sshPort,
  onClose,
}: Pick<RemoteDevelopment, 'editorUrl' | 'editorSessionId' | 'status' | 'sshPort'> &
  SharedRemoteSessionOptionProps & { hasSSHKeys: boolean }) => {
  const tooltipTitle =
    status?.state !== 'RUNNING'
      ? 'Session is not running'
      : !hasSSHKeys
        ? 'No SSH keys found'
        : !sshPort
          ? 'SSH port not found'
          : '';
  const launchUrl = `://vscode-remote/ssh-remote+anadev@${editorSessionId}.ssh-editor.${process.env.GATSBY_ENV}.rendered.ai:${sshPort}/ana/`;

  const editorLaunchDisabled = !hasSSHKeys || status?.state !== 'RUNNING' || !sshPort;

  const onSessionLaunch = (launchMethod: string) => {
    posthog.capture('user_launch_remote-session', {
      channelId,
      editorSessionId,
      launchMethod,
    });
    onClose();
  };
  return (
    <>
      <ExternalLink unstyled href={editorUrl}>
        <MenuItem
          onClick={() => {
            onSessionLaunch('browser');
          }}
        >
          <Typography variant="caption2">Launch session in browser</Typography>
        </MenuItem>
      </ExternalLink>
      <Tooltip variant="secondary" title={tooltipTitle}>
        <div>
          <MenuItem
            disabled={editorLaunchDisabled}
            onClick={() => {
              onSessionLaunch('cursor');
              window.open(`cursor${launchUrl}`, '_self');
            }}
          >
            <Typography variant="caption2">Launch session in Cursor</Typography>
          </MenuItem>
        </div>
      </Tooltip>
      <Tooltip variant="secondary" title={tooltipTitle}>
        <div>
          <MenuItem
            disabled={editorLaunchDisabled}
            onClick={() => {
              onSessionLaunch('vscode');
              window.open(`vscode${launchUrl}`, '_self');
            }}
          >
            <Typography variant="caption2">Launch session in VSCode</Typography>
          </MenuItem>
        </div>
      </Tooltip>
      <Tooltip variant="secondary" title={tooltipTitle}>
        <div>
          <MenuItem
            disabled={editorLaunchDisabled}
            onClick={() => {
              onSessionLaunch('windsurf');
              window.open(`windsurf${launchUrl}`, '_self');
            }}
          >
            <Typography variant="caption2">Launch session in Windsurf</Typography>
          </MenuItem>
        </div>
      </Tooltip>
    </>
  );
};

const StartSessionOption = ({
  channelId,
  editorSessionId,
  editorUrl,
  onClose,
}: Pick<RemoteDevelopment, 'editorSessionId' | 'editorUrl'> & SharedRemoteSessionOptionProps) => {
  const [startSession] = useStartRemoteDevelopmentMutation();
  const onClick = () => {
    posthog.capture('user_start_remote-session', { channelId, editorSessionId });
    posthog.capture('user_launch_remote-session', { channelId, editorSessionId });
    void startSession({ variables: { editorSessionId } });
    if (typeof window !== 'undefined') {
      window.open(editorUrl, '_blank');
    }
    if (onClose) {
      onClose();
    }
  };
  return (
    <MenuItem onClick={onClick}>
      <Typography variant="caption2">Start remote session</Typography>
    </MenuItem>
  );
};

const StopSessionOption = ({
  channelId,
  editorSessionId,
  onClose,
}: Pick<RemoteDevelopment, 'editorSessionId'> & SharedRemoteSessionOptionProps) => {
  const [stopSession] = useStopRemoteDevelopmentMutation();
  const { showConfirmation } = useConfirmation();
  const onClick = () => {
    if (onClose) {
      onClose();
    }
    void showConfirmation({
      onAffirm: () => {
        posthog.capture('user_stop_remote-session', { channelId, editorSessionId });
        void stopSession({ variables: { editorSessionId } });
      },
      message: `Are you sure you want to stop the remote session: ${editorSessionId}?`,
    });
  };
  return (
    <MenuItem onClick={onClick}>
      <Typography variant="caption2">Stop remote session</Typography>
    </MenuItem>
  );
};

const ShareSessionOption = ({
  editorSessionId,
  onClose,
}: Pick<RemoteDevelopment, 'editorSessionId'> & Partial<SharedRemoteSessionOptionProps>) => {
  const { showModal } = useModal({
    component: ShareRemoteSessionModal,
    componentProps: {
      editorSessionId,
    },
    modalProps: {
      title: 'Share remote session',
    },
  });
  return (
    <MenuItem
      onClick={() => {
        onClose();
        showModal();
      }}
    >
      <Typography variant="caption2">Share remote session</Typography>
    </MenuItem>
  );
};
